import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Plus, Loader2, Building2, FileSpreadsheet, FileText, Upload, Trash2, CheckCircle2,
  BookOpen, Files, ListPlus, X,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { NarrativePromptEditor } from "@/components/company/NarrativePromptEditor";

export default function Companies() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [uploadingId, setUploadingId] = useState(null);
  const [uploadingCritId, setUploadingCritId] = useState(null);
  const fileInputs = useRef({});
  const critInputs = useRef({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/companies");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error("No se pudieron cargar las compañías");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createCompany = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await api.post("/companies", { name: newName.trim() });
      setNewName("");
      toast.success("Compañía creada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear");
    } finally {
      setCreating(false);
    }
  };

  const onPickFile = (companyId) => {
    const input = fileInputs.current[companyId];
    if (input) input.click();
  };

  const onUploadBaremo = async (companyId, file) => {
    if (!file) return;
    setUploadingId(companyId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/companies/${companyId}/baremo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180_000,
      });
      toast.success(`Baremo cargado · ${r.data.entries_count} entradas (${r.data.format.toUpperCase()})`);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail
        || (err?.code === "ECONNABORTED" ? "El servidor tardó demasiado. Compruebo si el baremo se guardó igualmente…" : "No se pudo subir el baremo. Compruebo si se guardó igualmente…");
      toast.error(msg);
      load();
    } finally {
      setUploadingId(null);
    }
  };

  const onUploadCriterios = async (companyId, file) => {
    if (!file) return;
    setUploadingCritId(companyId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/companies/${companyId}/criterios`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180_000,
      });
      toast.success(`Criterios técnicos cargados · ${r.data.chars} caracteres`);
      load();
    } catch (err) {
      // Si el backend completó la escritura pero la respuesta no llegó (timeout
      // del proxy, conexión interrumpida, etc.), recargamos igualmente para que
      // el usuario vea si los criterios sí están persistidos.
      const msg = err?.response?.data?.detail
        || (err?.code === "ECONNABORTED" ? "El servidor tardó demasiado. Recargo la lista por si se guardó igualmente…" : "No se pudieron subir los criterios. Recargo la lista por si se guardó igualmente…");
      toast.error(msg);
      load();
    } finally {
      setUploadingCritId(null);
    }
  };

  // === Baremos nombrados múltiples (genérico, p. ej. Línea Directa con 3) ===
  const [namedBaremoName, setNamedBaremoName] = useState({});  // { companyId: "Baremo X 2026" }
  const [uploadingNamedId, setUploadingNamedId] = useState(null);

  const onUploadNamedBaremo = async (companyId, file) => {
    const name = (namedBaremoName[companyId] || "").trim();
    if (!name) {
      toast.error("Indica primero el nombre del baremo");
      return;
    }
    if (!file) return;
    setUploadingNamedId(companyId);
    try {
      const fd = new FormData();
      fd.append("name", name);
      fd.append("file", file);
      const r = await api.post(`/companies/${companyId}/baremos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180_000,
      });
      toast.success(`${r.data.replaced ? "Sustituido" : "Añadido"} «${r.data.name}» · ${r.data.entries_count} entradas`);
      setNamedBaremoName((m) => ({ ...m, [companyId]: "" }));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo subir el baremo");
    } finally {
      setUploadingNamedId(null);
    }
  };

  const onDeleteNamedBaremo = async (companyId, baremoId, baremoName) => {
    if (!window.confirm(`¿Eliminar el baremo «${baremoName}»? Los expedientes que lo tenían asignado se quedarán sin baremo.`)) return;
    try {
      await api.delete(`/companies/${companyId}/baremos/${baremoId}`);
      toast.success("Baremo eliminado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const onDeleteCriterios = async (companyId) => {
    if (!window.confirm("¿Quitar el documento de criterios técnicos? La IA dejará de consultarlo para esta compañía.")) return;
    try {
      await api.delete(`/companies/${companyId}/criterios`);
      toast.success("Criterios técnicos eliminados");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const [tplCompanyId, setTplCompanyId] = useState(null);
  const [tplName, setTplName] = useState("");
  const [tplFile, setTplFile] = useState(null);
  const [tplUploading, setTplUploading] = useState(false);

  const openTplDialog = (cid) => { setTplCompanyId(cid); setTplName(""); setTplFile(null); };
  const closeTplDialog = () => { setTplCompanyId(null); setTplName(""); setTplFile(null); };

  const submitTpl = async (e) => {
    e?.preventDefault?.();
    if (!tplCompanyId) return;
    if (!tplName.trim()) { toast.error("Indica el nombre de la modalidad"); return; }
    if (!tplFile) { toast.error("Selecciona el PDF/DOCX/TXT del condicionado"); return; }
    setTplUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", tplFile);
      fd.append("name", tplName.trim());
      const r = await api.post(`/companies/${tplCompanyId}/policy-templates`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180_000,
      });
      toast.success(`Modalidad "${r.data.template.name}" añadida · ${r.data.template.chars} caracteres`);
      closeTplDialog();
      load();
    } catch (err) {
      // Idem: si el doc llegó a guardarse en backend pero el cliente recibió
      // error (timeout/red), recargamos para que aparezca el chip.
      const msg = err?.response?.data?.detail
        || (err?.code === "ECONNABORTED" ? "El servidor tardó demasiado en responder. Compruebo si se guardó igualmente…" : "No se pudo subir la modalidad. Compruebo si se guardó igualmente…");
      toast.error(msg);
      closeTplDialog();
      load();
    } finally {
      setTplUploading(false);
    }
  };

  const deleteTpl = async (companyId, tplId, name) => {
    if (!window.confirm(`¿Eliminar la modalidad "${name}"?`)) return;
    try {
      await api.delete(`/companies/${companyId}/policy-templates/${tplId}`);
      toast.success("Modalidad eliminada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const onDelete = async (companyId, name) => {
    if (!window.confirm(`¿Eliminar la compañía "${name}" y su baremo?`)) return;
    try {
      await api.delete(`/companies/${companyId}`);
      toast.success("Compañía eliminada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="companies-page">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-[#2C2C2A]">Compañías, baremos y modalidades</h1>
          <p className="text-[#7A786F] mt-1 text-sm">
            Gestiona las compañías aseguradoras con las que trabajáis. Sube el <strong>baremo</strong> (PDF/Excel) para que la IA aplique precios exactos y los <strong>condicionados de cada modalidad de póliza</strong> (PDF/DOCX) para que pueda referenciar coberturas y exclusiones citando el artículo aplicable.
          </p>
        </div>
      </div>

      <form
        onSubmit={createCompany}
        className="flex gap-2 bg-white border border-[#E5E2D8] rounded-[14px] p-4 mb-6"
        data-testid="new-company-form"
      >
        <Input
          placeholder="Nombre de la compañía (ej. Mapfre)"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          className="rounded-[10px] border-[#E5E2D8]"
          data-testid="new-company-name-input"
        />
        <Button type="submit" disabled={creating || !newName.trim()} className="btn-eks-primary rounded-[10px]" data-testid="new-company-submit">
          {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
          Añadir
        </Button>
      </form>

      {loading ? (
        <div className="text-center text-[#7A786F] py-10">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E5E2D8] rounded-[14px] p-10 text-center text-[#7A786F]" data-testid="companies-empty">
          <Building2 className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <div>Aún no hay compañías cargadas.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((c) => (
            <div key={c.id} className="bg-white border border-[#E5E2D8] rounded-[14px] p-4" data-testid={`company-row-${c.slug}`}>
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-[10px] bg-[#FAECE7] flex items-center justify-center shrink-0">
                  <Building2 className="h-5 w-5 text-[#D85A30]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#2C2C2A] truncate">{c.name}</div>
                  <div className="text-[12px] text-[#7A786F] flex items-center gap-2 mt-0.5 flex-wrap">
                    {c.has_baremo ? (
                      <>
                        {c.baremo_format === "xlsx" ? (
                          <FileSpreadsheet className="h-3.5 w-3.5 text-[#1D9E75]" />
                        ) : (
                          <FileText className="h-3.5 w-3.5 text-[#1D9E75]" />
                        )}
                        <span className="truncate max-w-[240px]">{c.baremo_filename}</span>
                        <Badge variant="outline" className="bg-[#E6F5EF] text-[#1D9E75] border-transparent rounded-full text-[10px] py-0">
                          <CheckCircle2 className="h-3 w-3 mr-1" /> {c.baremo_entries_count} partidas
                        </Badge>
                      </>
                    ) : (
                      <span className="italic">Sin baremo cargado</span>
                    )}
                  </div>
                </div>
                <input
                  ref={(el) => { fileInputs.current[c.id] = el; }}
                  type="file"
                  accept=".pdf,.xlsx,.xlsm,.csv,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                  onChange={(e) => onUploadBaremo(c.id, e.target.files?.[0])}
                  className="hidden"
                  data-testid={`company-file-input-${c.slug}`}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPickFile(c.id)}
                  disabled={uploadingId === c.id}
                  className="rounded-[10px] border-[#E5E2D8]"
                  data-testid={`company-upload-btn-${c.slug}`}
                >
                  {uploadingId === c.id ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
                  {c.has_baremo ? "Actualizar baremo" : "Subir baremo"}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(c.id, c.name)}
                  className="text-[#B43F3F] hover:bg-[#FBEEEA] rounded-[10px]"
                  data-testid={`company-delete-btn-${c.slug}`}
                  title="Eliminar compañía"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              {/* Criterios técnicos / condicionado de póliza */}
              <div className="mt-3 pt-3 border-t border-dashed border-[#E5E2D8] flex items-center gap-3 flex-wrap">
                <div className="h-8 w-8 rounded-[10px] bg-[#EAF1FB] flex items-center justify-center shrink-0">
                  <BookOpen className="h-4 w-4 text-[#2E5AAC]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-medium text-[#2C2C2A]">Criterios técnicos / condicionado de póliza</div>
                  <div className="text-[11px] text-[#7A786F] flex items-center gap-2 mt-0.5 flex-wrap">
                    {c.has_criterios ? (
                      <>
                        <FileText className="h-3 w-3 text-[#2E5AAC]" />
                        <span className="truncate max-w-[260px]">{c.criterios_filename}</span>
                        <Badge variant="outline" className="bg-[#EAF1FB] text-[#2E5AAC] border-transparent rounded-full text-[10px] py-0">
                          <CheckCircle2 className="h-3 w-3 mr-1" /> {c.criterios_chars} caracteres · la IA lo consultará
                        </Badge>
                      </>
                    ) : (
                      <span className="italic">Sin documento de criterios. Sube un PDF/DOCX/TXT con el condicionado para que la IA referencie coberturas.</span>
                    )}
                  </div>
                </div>
                <input
                  ref={(el) => { critInputs.current[c.id] = el; }}
                  type="file"
                  accept=".pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                  onChange={(e) => onUploadCriterios(c.id, e.target.files?.[0])}
                  className="hidden"
                  data-testid={`company-criterios-input-${c.slug}`}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => critInputs.current[c.id]?.click()}
                  disabled={uploadingCritId === c.id}
                  className="rounded-[10px] border-[#E5E2D8]"
                  data-testid={`company-criterios-upload-${c.slug}`}
                >
                  {uploadingCritId === c.id ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
                  {c.has_criterios ? "Actualizar criterios" : "Subir criterios"}
                </Button>
                {c.has_criterios && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeleteCriterios(c.id)}
                    className="text-[#B43F3F] hover:bg-[#FBEEEA] rounded-[10px]"
                    data-testid={`company-criterios-delete-${c.slug}`}
                    title="Quitar criterios"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {/* Modalidades de póliza (Hogar Flexible L/XL/XXL, MHS Esencial, …) */}
              <div className="mt-3 pt-3 border-t border-dashed border-[#E5E2D8]">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="h-8 w-8 rounded-[10px] bg-[#FAECE7] flex items-center justify-center shrink-0">
                    <Files className="h-4 w-4 text-[#D85A30]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-medium text-[#2C2C2A]">Modalidades de póliza para esta compañía</div>
                    <div className="text-[11px] text-[#7A786F] mt-0.5">
                      {(c.policy_templates?.length || 0) === 0
                        ? "Sube los condicionados de cada modalidad (p. ej. Hogar Flexible L/XL/XXL, MHS Esencial/Integral/Ampliado…). Aparecerán en el dropdown «Modalidad de póliza suscrita» al crear un expediente."
                        : `${c.policy_templates.length} modalidad(es) cargada(s) · disponibles en el dropdown «Modalidad de póliza suscrita»`}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openTplDialog(c.id)}
                    className="rounded-[10px] border-[#E5E2D8]"
                    data-testid={`company-policy-add-${c.slug}`}
                  >
                    <ListPlus className="h-3.5 w-3.5 mr-1.5" />
                    Añadir modalidad
                  </Button>
                </div>
                {(c.policy_templates?.length || 0) > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {c.policy_templates.map((t) => (
                      <div key={t.id} className="inline-flex items-center gap-1.5 bg-[#FAECE7] text-[#7A2A14] border border-[#F2DAD0] rounded-full px-2.5 py-1 text-[11px]" data-testid={`company-policy-chip-${c.slug}-${t.id}`}>
                        <FileText className="h-3 w-3" />
                        <span className="font-medium">{t.name}</span>
                        <span className="text-[10px] text-[#9A6957]">· {t.chars} c</span>
                        <button
                          onClick={() => deleteTpl(c.id, t.id, t.name)}
                          className="ml-1 text-[#9A6957] hover:text-[#B43F3F]"
                          title="Eliminar modalidad"
                          data-testid={`company-policy-delete-${c.slug}-${t.id}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* === Prompt IA de narrativa (Causas y circunstancias) === */}
              <div className="mt-3 pt-3 border-t border-dashed border-[#E5E2D8] flex items-center gap-3 flex-wrap">
                <div className="h-8 w-8 rounded-[10px] bg-[#FAECE7] flex items-center justify-center shrink-0">
                  <FileText className="h-4 w-4 text-[#D85A30]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-medium text-[#2C2C2A]">Prompt IA — Causas y circunstancias</div>
                  <div className="text-[11px] text-[#7A786F] mt-0.5">
                    {c.has_narrative_prompt
                      ? "Prompt custom activo · la IA usa las reglas específicas de esta compañía."
                      : "Usando plantilla por defecto del sistema. Puedes personalizar cómo redacta la IA para esta compañía."}
                  </div>
                </div>
                <NarrativePromptEditor
                  companyId={c.id}
                  companyName={c.name}
                  companySlug={c.slug}
                />
              </div>

              {/* === BAREMOS NOMBRADOS (múltiples) === */}
              <div className="mt-4 pt-4 border-t border-[#E5E2D8]">
                <div className="flex items-start gap-2 flex-wrap">
                  <ListPlus className="h-4 w-4 text-[#D85A30] shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-[200px]">
                    <div className="text-sm font-medium text-[#2C2C2A]">Baremos disponibles para esta compañía</div>
                    <div className="text-xs text-[#7A786F] mt-0.5">
                      {(c.baremos?.length || 0) === 0
                        ? "Sube uno o varios baremos con nombre (p. ej. «Baremo Interpartner Hogar 2026», «Homeserve 2026»). El perito elegirá cuál aplicar al crear cada expediente."
                        : `${c.baremos.length} baremo(s) cargado(s) · disponibles en el dropdown «Baremo a aplicar»`}
                    </div>
                  </div>
                </div>
                <div className="mt-2 flex gap-2 flex-wrap items-center">
                  <input
                    type="text"
                    value={namedBaremoName[c.id] || ""}
                    onChange={(e) => setNamedBaremoName((m) => ({ ...m, [c.id]: e.target.value }))}
                    placeholder="Nombre del baremo (p. ej. Homeserve 2026)"
                    className="flex-1 min-w-[260px] bg-[#FBFAF6] text-[#2C2C2A] text-sm px-3 py-2 rounded-[10px] border border-[#E5E2D8] focus:border-[#D85A30] outline-none"
                    data-testid={`company-named-baremo-name-${c.slug}`}
                  />
                  <input
                    id={`named-baremo-input-${c.id}`}
                    type="file"
                    accept=".pdf,.xlsx,.xls,.csv,application/pdf"
                    onChange={(e) => { onUploadNamedBaremo(c.id, e.target.files?.[0]); e.target.value = ""; }}
                    className="hidden"
                    data-testid={`company-named-baremo-input-${c.slug}`}
                  />
                  <Button
                    size="sm"
                    onClick={() => {
                      const name = (namedBaremoName[c.id] || "").trim();
                      if (!name) { toast.error("Indica primero el nombre del baremo"); return; }
                      document.getElementById(`named-baremo-input-${c.id}`)?.click();
                    }}
                    disabled={uploadingNamedId === c.id}
                    className="bg-[#D85A30] hover:bg-[#C04D27] text-white rounded-[10px]"
                    data-testid={`company-named-baremo-upload-${c.slug}`}
                  >
                    {uploadingNamedId === c.id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}
                    Añadir baremo
                  </Button>
                </div>
                {(c.baremos?.length || 0) > 0 && (
                  <div className="mt-3 space-y-1.5">
                    {c.baremos.map((b) => (
                      <div key={b.id} className="flex items-center gap-2 bg-[#FBFAF6] border border-[#E5E2D8] rounded-[10px] p-2 text-sm" data-testid={`company-named-baremo-row-${c.slug}-${b.id}`}>
                        <ListPlus className="h-3.5 w-3.5 text-[#D85A30] shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-[#2C2C2A] truncate">{b.name}</div>
                          <div className="text-[10px] text-[#7A786F] font-mono truncate">
                            {b.filename} · {b.items_count} partidas · {b.format?.toUpperCase()}
                            {b.uploaded_at ? ` · ${new Date(b.uploaded_at).toLocaleDateString("es-ES")}` : ""}
                          </div>
                        </div>
                        <button
                          onClick={() => onDeleteNamedBaremo(c.id, b.id, b.name)}
                          className="text-[#7A786F] hover:text-[#B43F3F] p-1"
                          title="Eliminar"
                          data-testid={`company-named-baremo-delete-${c.slug}-${b.id}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog: añadir plantilla de póliza */}
      <Dialog open={!!tplCompanyId} onOpenChange={(v) => { if (!v) closeTplDialog(); }}>
        <DialogContent data-testid="policy-tpl-dialog" className="rounded-[16px]">
          <DialogHeader>
            <DialogTitle className="font-heading">Añadir modalidad de póliza</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitTpl} className="space-y-4">
            <div>
              <Label htmlFor="tpl-name">Nombre de la modalidad *</Label>
              <Input
                id="tpl-name"
                required
                value={tplName}
                onChange={(e) => setTplName(e.target.value)}
                placeholder="P. ej. Hogar Flexible XXL, MHS Esencial, …"
                data-testid="policy-tpl-name-input"
                className="rounded-[12px] border-[#E5E2D8]"
              />
              <p className="text-[11px] text-[#7A786F] mt-1">Aparecerá como opción en el dropdown «Modalidad de póliza suscrita» al crear un expediente.</p>
            </div>
            <div>
              <Label htmlFor="tpl-file">Condicionado (PDF, DOCX o TXT) *</Label>
              <Input
                id="tpl-file"
                type="file"
                accept=".pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                onChange={(e) => setTplFile(e.target.files?.[0] || null)}
                data-testid="policy-tpl-file-input"
                className="rounded-[12px] border-[#E5E2D8]"
              />
              <p className="text-[11px] text-[#7A786F] mt-1">El PDF debe tener texto seleccionable (no escaneado). Máx. 25 MB.</p>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" className="rounded-[12px] border-[#E5E2D8]" onClick={closeTplDialog}>Cancelar</Button>
              <Button type="submit" disabled={tplUploading} className="btn-eks-primary rounded-[12px]" data-testid="policy-tpl-submit">
                {tplUploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                Subir modalidad
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
